import { ADUModel, PoolModel, DesignObject, PoolEquipmentModel, ADUEquipmentModel } from '../store/designStore'

// Base pricing data (in USD)
export const PRICING_DATA = {
  ADU: {
    // Base cost per square foot
    baseCostPerSqFt: 250,
    
    // Material multipliers and costs
    materials: {
      walls: {
        '#f5f5f5': { multiplier: 1.0, costPerSqFt: 8 },    // Standard Siding
        '#e2e8f0': { multiplier: 1.2, costPerSqFt: 12 },   // Fiber Cement
        '#8b4513': { multiplier: 1.4, costPerSqFt: 15 },   // Wood Siding
        '#654321': { multiplier: 1.6, costPerSqFt: 18 },   // Cedar Siding
        '#2d3748': { multiplier: 1.3, costPerSqFt: 14 },   // Metal Siding
        '#4a5568': { multiplier: 1.8, costPerSqFt: 22 },   // Brick Veneer
        '#1a202c': { multiplier: 2.2, costPerSqFt: 28 },   // Stone Veneer
      },
      roof: {
        '#808080': { multiplier: 1.0, costPerSqFt: 4 },    // Asphalt Shingles
        '#696969': { multiplier: 1.2, costPerSqFt: 6 },    // Architectural Shingles
        '#2f4f4f': { multiplier: 1.5, costPerSqFt: 8 },    // Metal Roofing
        '#8b4513': { multiplier: 2.0, costPerSqFt: 12 },   // Cedar Shakes
        '#654321': { multiplier: 2.5, costPerSqFt: 15 },   // Slate Tiles
        '#4a5568': { multiplier: 1.8, costPerSqFt: 10 },   // Clay Tiles
        '#1a202c': { multiplier: 2.2, costPerSqFt: 12 },   // Premium Metal
      },
      windows: {
        '#87ceeb': { multiplier: 1.0, costPerWindow: 300 }, // Standard Glass
        '#4169e1': { multiplier: 1.3, costPerWindow: 450 }, // Double Pane
        '#1e90ff': { multiplier: 1.7, costPerWindow: 600 }, // Low-E Glass
        '#0066cc': { multiplier: 2.2, costPerWindow: 800 }, // Triple Pane
        '#003d7a': { multiplier: 2.8, costPerWindow: 1000 }, // Premium Low-E
      },
      door: {
        '#8b4513': { multiplier: 1.0, cost: 400 },    // Standard Wood
        '#654321': { multiplier: 1.5, cost: 800 },    // Solid Wood
        '#2f4f4f': { multiplier: 1.2, cost: 600 },    // Steel Entry
        '#4a5568': { multiplier: 1.4, cost: 700 },    // Fiberglass
        '#1a202c': { multiplier: 2.0, cost: 1200 },   // Premium Wood
        '#744210': { multiplier: 2.8, cost: 1800 },   // Custom Wood
      },
      trim: {
        '#ffffff': { multiplier: 1.0, costPerLinearFt: 3 }, // Vinyl Trim
        '#f5f5f5': { multiplier: 1.3, costPerLinearFt: 5 }, // Composite Trim
        '#e2e8f0': { multiplier: 1.6, costPerLinearFt: 7 }, // Wood Trim
        '#d1d5db': { multiplier: 2.0, costPerLinearFt: 9 }, // Cedar Trim
        '#9ca3af': { multiplier: 1.5, costPerLinearFt: 6 }, // Aluminum Trim
      }
    },
    
    // Feature costs
    features: {
      bedroom: 8000,      // Cost per bedroom
      bathroom: 12000,    // Cost per bathroom
      kitchen: 25000,     // Kitchen package
      livingRoom: 5000,   // Living room upgrade
    },
    
    // Labor and permits
    laborMultiplier: 0.4,  // 40% of material cost
    permitCost: 2500,
    designCost: 3500,
    
    // Size-based adjustments
    sizeAdjustments: {
      small: 0.9,    // < 400 sq ft
      medium: 1.0,   // 400-800 sq ft
      large: 1.1,    // > 800 sq ft
    }
  },
  
  POOL: {
    // Base cost per square foot of pool surface
    baseCostPerSqFt: 75,
    
    // Material multipliers
    materials: {
      water: {
        '#6dd5ed': 1.0,    // Standard blue
        '#4169e1': 1.0,    // Deep blue
        '#00ced1': 1.1,    // Turquoise
      },
      coping: {
        '#a0a0a0': 1.0,    // Standard concrete
        '#d3d3d3': 1.2,    // Natural stone
        '#8b7355': 1.4,    // Travertine
        '#2f4f4f': 1.3,    // Granite
      },
      deck: {
        '#deb887': 1.0,    // Standard concrete
        '#d2691e': 1.3,    // Wood decking
        '#8b7355': 1.4,    // Pavers
        '#696969': 1.2,    // Stamped concrete
      }
    },
    
    // Feature costs
    features: {
      spa: 15000,         // Attached spa
      lighting: 3500,     // LED lighting package
      heating: 8000,      // Pool heater system
    },
    
    // Equipment and installation
    equipmentCost: 12000,   // Pump, filter, etc.
    excavationPerSqFt: 15,  // Excavation cost
    laborMultiplier: 0.5,   // 50% of material cost
    permitCost: 1500,
    
    // Size-based adjustments
    sizeAdjustments: {
      small: 0.9,    // < 300 sq ft
      medium: 1.0,   // 300-600 sq ft
      large: 1.1,    // > 600 sq ft
    }
  },
  
  // Regional multipliers (can be expanded)
  regions: {
    california: 1.3,
    texas: 1.0,
    florida: 1.1,
    newyork: 1.4,
    default: 1.0
  }
}

export interface PricingBreakdown {
  itemName: string
  basePrice: number
  materialCosts: number
  featureCosts: number
  laborCosts: number
  permitCosts: number
  totalPrice: number
  pricePerSqFt: number
  breakdown: {
    materials: { [key: string]: number }
    features: { [key: string]: number }
    labor: number
    permits: number
    design?: number
    equipment?: number
    excavation?: number
  }
}

export interface ProjectQuote {
  objects: PricingBreakdown[]
  subtotal: number
  tax: number
  total: number
  timeline: string
  warranty: string
  notes: string[]
}

export class PricingEngine {
  private region: keyof typeof PRICING_DATA.regions = 'default'
  private taxRate: number = 0.08 // 8% default tax rate

  constructor(region?: keyof typeof PRICING_DATA.regions, taxRate?: number) {
    if (region) this.region = region
    if (taxRate) this.taxRate = taxRate
  }

  calculateADUPrice(adu: ADUModel): PricingBreakdown {
    const { dimensions, materials, features } = adu
    const pricing = PRICING_DATA.ADU
    
    // Calculate square footage
    const sqFt = dimensions.width * dimensions.depth
    
    // Base price
    const basePrice = sqFt * pricing.baseCostPerSqFt
    
    // Material costs with detailed pricing
    const wallMaterial = (pricing.materials.walls as any)[materials.walls] || pricing.materials.walls['#f5f5f5']
    const roofMaterial = (pricing.materials.roof as any)[materials.roof] || pricing.materials.roof['#808080']
    const windowMaterial = (pricing.materials.windows as any)[materials.windows] || pricing.materials.windows['#87ceeb']
    const doorMaterial = (pricing.materials.door as any)[materials.door] || pricing.materials.door['#8b4513']
    const trimMaterial = (pricing.materials.trim as any)[materials.trim] || pricing.materials.trim['#ffffff']
    
    // Calculate actual material costs
    const wallArea = (dimensions.width * dimensions.height * 2) + (dimensions.depth * dimensions.height * 2) // 4 walls
    const roofArea = dimensions.width * dimensions.depth // Roof area
    const estimatedWindows = Math.max(4, Math.floor(sqFt / 100)) // Estimate windows based on size
    const trimLength = (dimensions.width + dimensions.depth) * 4 // Perimeter estimate
    
    const wallCosts = wallArea * wallMaterial.costPerSqFt
    const roofCosts = roofArea * roofMaterial.costPerSqFt
    const windowCosts = estimatedWindows * windowMaterial.costPerWindow
    const doorCosts = doorMaterial.cost
    const trimCosts = trimLength * trimMaterial.costPerLinearFt
    
    const materialCosts = wallCosts + roofCosts + windowCosts + doorCosts + trimCosts
    
    // Feature costs
    const featureCosts = 
      (features.bedrooms * pricing.features.bedroom) +
      (features.bathrooms * pricing.features.bathroom) +
      (features.kitchen ? pricing.features.kitchen : 0) +
      (features.livingRoom ? pricing.features.livingRoom : 0)
    
    // Size adjustment
    const sizeCategory = sqFt < 400 ? 'small' : sqFt > 800 ? 'large' : 'medium'
    const sizeMultiplier = pricing.sizeAdjustments[sizeCategory]
    
    // Labor costs
    const laborCosts = (materialCosts + featureCosts) * pricing.laborMultiplier
    
    // Total before regional adjustment
    const subtotal = (materialCosts + featureCosts + laborCosts + pricing.permitCost + pricing.designCost) * sizeMultiplier
    
    // Regional adjustment
    const regionalMultiplier = PRICING_DATA.regions[this.region]
    const totalPrice = subtotal * regionalMultiplier
    
    return {
      itemName: adu.name,
      basePrice,
      materialCosts,
      featureCosts,
      laborCosts,
      permitCosts: pricing.permitCost,
      totalPrice,
      pricePerSqFt: totalPrice / sqFt,
      breakdown: {
        materials: {
          walls: wallCosts,
          roof: roofCosts,
          windows: windowCosts,
          door: doorCosts,
          trim: trimCosts,
        },
        features: {
          bedrooms: features.bedrooms * pricing.features.bedroom,
          bathrooms: features.bathrooms * pricing.features.bathroom,
          kitchen: features.kitchen ? pricing.features.kitchen : 0,
          livingRoom: features.livingRoom ? pricing.features.livingRoom : 0,
        },
        labor: laborCosts,
        permits: pricing.permitCost,
        design: pricing.designCost,
      }
    }
  }

  calculatePoolPrice(pool: PoolModel): PricingBreakdown {
    const { dimensions, materials, features } = pool
    const pricing = PRICING_DATA.POOL
    
    // Calculate surface area
    const sqFt = dimensions.width * dimensions.length
    
    // Base price
    const basePrice = sqFt * pricing.baseCostPerSqFt
    
    // Material costs
    const copingMultiplier = (pricing.materials.coping as any)[materials.coping] || 1.0
    const deckMultiplier = (pricing.materials.deck as any)[materials.deck] || 1.0
    
    const materialCosts = basePrice * (
      (copingMultiplier * 0.3) +  // 30% coping
      (deckMultiplier * 0.7)     // 70% deck/surface
    )
    
    // Feature costs
    const featureCosts = 
      (features.spa ? pricing.features.spa : 0) +
      (features.lighting ? pricing.features.lighting : 0) +
      (features.heating ? pricing.features.heating : 0)
    
    // Equipment and excavation
    const excavationCosts = sqFt * pricing.excavationPerSqFt
    const equipmentCosts = pricing.equipmentCost
    
    // Size adjustment
    const sizeCategory = sqFt < 300 ? 'small' : sqFt > 600 ? 'large' : 'medium'
    const sizeMultiplier = pricing.sizeAdjustments[sizeCategory]
    
    // Labor costs
    const laborCosts = (materialCosts + featureCosts + equipmentCosts) * pricing.laborMultiplier
    
    // Total before regional adjustment
    const subtotal = (materialCosts + featureCosts + laborCosts + excavationCosts + equipmentCosts + pricing.permitCost) * sizeMultiplier
    
    // Regional adjustment
    const regionalMultiplier = PRICING_DATA.regions[this.region]
    const totalPrice = subtotal * regionalMultiplier
    
    return {
      itemName: pool.name,
      basePrice,
      materialCosts,
      featureCosts,
      laborCosts,
      permitCosts: pricing.permitCost,
      totalPrice,
      pricePerSqFt: totalPrice / sqFt,
      breakdown: {
        materials: {
          coping: basePrice * copingMultiplier * 0.3,
          deck: basePrice * deckMultiplier * 0.7,
        },
        features: {
          spa: features.spa ? pricing.features.spa : 0,
          lighting: features.lighting ? pricing.features.lighting : 0,
          heating: features.heating ? pricing.features.heating : 0,
        },
        labor: laborCosts,
        permits: pricing.permitCost,
        equipment: equipmentCosts,
        excavation: excavationCosts,
      }
    }
  }

  generateProjectQuote(objects: DesignObject[]): ProjectQuote {
    const breakdowns: PricingBreakdown[] = []
    
    for (const obj of objects) {
      if (obj.type === 'ADU') {
        breakdowns.push(this.calculateADUPrice(obj as ADUModel))
      } else if (obj.type === 'Pool') {
        breakdowns.push(this.calculatePoolPrice(obj as PoolModel))
      } else if ('equipmentType' in obj) {
        // Handle equipment items
        if ('poolId' in obj) {
          breakdowns.push(this.calculatePoolEquipmentPrice(obj as PoolEquipmentModel))
        } else if ('aduId' in obj) {
          breakdowns.push(this.calculateADUEquipmentPrice(obj as ADUEquipmentModel))
        }
      }
    }
    
    const subtotal = breakdowns.reduce((sum, item) => sum + item.totalPrice, 0)
    const tax = subtotal * this.taxRate
    const total = subtotal + tax
    
    // Estimate timeline based on project complexity
    const hasADU = breakdowns.some(b => b.itemName.includes('ADU'))
    const hasPool = breakdowns.some(b => b.itemName.includes('Pool'))
    
    let timeline = '8-12 weeks'
    if (hasADU && hasPool) {
      timeline = '16-24 weeks'
    } else if (hasADU) {
      timeline = '12-16 weeks'
    } else if (hasPool) {
      timeline = '8-12 weeks'
    }
    
    const notes = [
      'Prices include materials, labor, and permits',
      'Final pricing may vary based on site conditions',
      'Timeline estimates include permit approval process',
      'All work comes with comprehensive warranty',
      'Free design consultation included'
    ]
    
    return {
      objects: breakdowns,
      subtotal,
      tax,
      total,
      timeline,
      warranty: '10-year structural, 2-year workmanship',
      notes
    }
  }

  // Equipment pricing methods
  calculatePoolEquipmentPrice(equipment: PoolEquipmentModel): PricingBreakdown {
    const equipmentPrices = {
      pump: { base: 800, labor: 400 },
      filter: { base: 600, labor: 300 },
      heater: { base: 2500, labor: 800 },
      electrical_panel: { base: 1200, labor: 600 },
      chlorinator: { base: 900, labor: 200 },
      automation_controller: { base: 1800, labor: 400 }
    }
    
    const pricing = equipmentPrices[equipment.equipmentType] || { base: 500, labor: 200 }
    const materialCost = pricing.base
    const laborCost = pricing.labor
    const totalPrice = materialCost + laborCost
    
    return {
      itemName: `${equipment.name} (Pool Equipment)`,
      basePrice: materialCost,
      materialCosts: materialCost,
      featureCosts: 0,
      laborCosts: laborCost,
      permitCosts: 0,
      totalPrice,
      pricePerSqFt: 0,
      breakdown: {
        materials: { [equipment.equipmentType]: materialCost },
        features: {},
        labor: laborCost,
        permits: 0,
        design: 0
      }
    }
  }
  
  calculateADUEquipmentPrice(equipment: ADUEquipmentModel): PricingBreakdown {
    const equipmentPrices: { [key: string]: { base: number; labor: number } } = {
      // Plumbing fixtures
      toilet: { base: 400, labor: 300 },
      sink_bathroom: { base: 300, labor: 200 },
      sink_kitchen: { base: 500, labor: 250 },
      shower: { base: 800, labor: 600 },
      bathtub: { base: 1200, labor: 800 },
      
      // Kitchen appliances
      refrigerator: { base: 1500, labor: 100 },
      stove: { base: 1200, labor: 300 },
      dishwasher: { base: 800, labor: 200 },
      microwave: { base: 400, labor: 100 },
      
      // Cabinets & storage
      kitchen_cabinets: { base: 3000, labor: 1200 },
      bathroom_vanity: { base: 800, labor: 400 },
      closet_system: { base: 1200, labor: 600 },
      
      // Windows & doors
      window_standard: { base: 400, labor: 200 },
      window_large: { base: 800, labor: 300 },
      entry_door: { base: 800, labor: 400 },
      interior_door: { base: 300, labor: 150 },
      
      // HVAC & utilities
      hvac_unit: { base: 3500, labor: 1200 },
      electrical_panel: { base: 800, labor: 600 },
      water_heater: { base: 1200, labor: 400 },
      
      // Lighting & electrical
      ceiling_light: { base: 150, labor: 100 },
      recessed_light: { base: 80, labor: 120 },
      outlet: { base: 25, labor: 75 }
    }
    
    const pricing = equipmentPrices[equipment.equipmentType] || { base: 200, labor: 100 }
    const materialCost = pricing.base
    const laborCost = pricing.labor
    const totalPrice = materialCost + laborCost
    
    return {
      itemName: `${equipment.name} (ADU Component)`,
      basePrice: materialCost,
      materialCosts: materialCost,
      featureCosts: 0,
      laborCosts: laborCost,
      permitCosts: 0,
      totalPrice,
      pricePerSqFt: 0,
      breakdown: {
        materials: { [equipment.equipmentType]: materialCost },
        features: {},
        labor: laborCost,
        permits: 0,
        design: 0
      }
    }
  }

  // Utility methods
  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  getEstimateRange(basePrice: number, variance: number = 0.15): string {
    const low = basePrice * (1 - variance)
    const high = basePrice * (1 + variance)
    return `${this.formatPrice(low)} - ${this.formatPrice(high)}`
  }
}

// Export singleton instance
export const pricingEngine = new PricingEngine()

// Export utility functions
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export const calculateSavings = (originalPrice: number, discountedPrice: number): number => {
  return ((originalPrice - discountedPrice) / originalPrice) * 100
}
